import * as Sentry from '@sentry/react';
import { commands } from '@/lib/platform/generated/bindings.gen';
import { getEntityRow, useEntityStore } from './entity-store';
import { useRevocationStore } from '@/lib/sharing/stores/use-revocation-store';
import { parseEntityEvent } from './entity-event-validators';
import type { RecordingStartedPayload } from './entity-events/recording';
import { invalidateCreditBalance } from '@/lib/billing/use-subscription';
import { useWorkspaceStore } from '@/stores/use-workspace-store';

/**
 * Applies a parsed sync event payload to the entity store.
 *
 * Called from SyncSystemContext when a KIND_SYNC_EVENT envelope is received.
 * The payload is already decoded JSON — no wire-format parsing here.
 *
 * When a DELETED event matches the currently-viewed resource (tracked via
 * useRevocationStore.viewedResourceId), the revocation modal is triggered
 * so the user is notified and redirected to the workspace home.
 *
 * Malformed envelopes (missing required fields, wrong field types) are dropped
 * rather than applied. A structured error is logged so operators can detect
 * server-side schema drift without silently corrupting client state.
 */
export function handleEntityEvent(payload: Record<string, unknown>): void {
  const parsed = parseEntityEvent(payload);
  if (!parsed) {
    Sentry.captureMessage('[entityEventHandler] dropped malformed entity event', 'error');
    console.error('[entityEventHandler] dropped malformed entity event:', payload);
    return;
  }

  const { id, kind, modelKind, modelId, data } = parsed;
  const entityState = useEntityStore.getState();

  // ── recording.started: cross-device suppression side-effect ─────────────
  // recording.started is not a store event — it is a cross-device suppression
  // signal (session 27) that must be forwarded to the Rust side. Best-effort:
  // failure means the local notifier may fire a duplicate notification.
  if (kind === 'recording.started') {
    const startedPayload = data as RecordingStartedPayload;
    commands.applyCrossDeviceSuppression(startedPayload)
      .then((res) => {
        if (res.status === 'error') {
          console.warn('[entityEventHandler] applyCrossDeviceSuppression failed', res.error);
        }
      })
      .catch((err) => {
        console.warn('[entityEventHandler] cross-device suppression failed', err);
      });
    return;
  }

  // ── Standard entity events (UPSERTED / DELETED) ──────────────────────────
  // All model kinds — document, collection, snapshot, recording — now use
  // bare UPSERTED/DELETED (S7 normalization). The reducer in entityStore.ts
  // handles each modelKind in the UPSERTED/DELETED branches.

  // Before applying a DELETED event, check if it matches the currently-viewed
  // resource. If so, read the title (while it still exists) and trigger the
  // revocation modal. Every kind has revocation UI except 'snapshot' (not an
  // independently-viewable resource) — gate out only that, rather than
  // allow-listing kinds one at a time. The allow-list drifted once already
  // (canvas/space/conversation silently missing) — that drift is the bug
  // this replaced.
  if (kind === 'DELETED' && modelKind !== 'snapshot') {
    const { viewedResourceId, setRevoked } = useRevocationStore.getState();
    if (viewedResourceId === modelId) {
      let title: string;
      if (modelKind === 'recording') {
        // RecordingMeta has no title field — D-16 says the title lives on
        // the linked document. Resolve via the recording's entityId; falls back
        // to '' when the doc has already been pruned or the recording was
        // ad-hoc (entityId === null).
        const entityId = entityState.recordings.get(modelId)?.entityId ?? null;
        title = entityId ? (entityState.entityMeta.get(entityId)?.title ?? '') : '';
      } else {
        title = getEntityRow(entityState, modelId)?.title ?? '';
      }
      setRevoked({ id: modelId, title });
    }
  }

  // A completed recording is what spends recording credits. Nothing else
  // refreshes the balance, so without this the meter stays stale until the
  // user reopens Settings.
  if (kind === 'UPSERTED' && modelKind === 'recording') {
    const status = (data as { status?: string } | null)?.status;
    if (status === 'complete') {
      void invalidateCreditBalance(useWorkspaceStore.getState().activeWorkspaceId);
    }
  }

  entityState.applyEvent({
    id,
    kind: kind as 'UPSERTED' | 'DELETED',
    modelKind: modelKind as 'document' | 'collection' | 'snapshot' | 'recording',
    modelId,
    data: (data as Record<string, unknown>) ?? null,
  });
}
